/**
 * F-505-FE-02: DoctorLayout
 *
 * Mobile-first layout for the doctor interface:
 * - Minimal navigation: back button (context-aware) + Clinical Interpreter wordmark + profile icon
 * - Touch-optimized: minimum 44px touch targets; 56px for critical actions
 * - Responsive container (max-w-2xl, tablet-friendly)
 * - Sticky top navigation bar with backdrop blur
 * - Subtle transitions (medical device UI — purposeful, not flashy)
 *
 * This replaces src/components/DoctorLayout.tsx (the F-404 scaffold).
 * The old file is preserved for backward compat during migration but this
 * canonical version lives in src/layouts/.
 */

import { type ReactNode } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { NotificationBadge } from '@/components/NotificationBadge';
import { useIdleTimeout } from '@/hooks/useIdleTimeout';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DoctorLayoutProps {
  children: ReactNode;
  /** Override the back button target. Pass null to hide back button entirely. */
  backTo?: string | null;
  /** Page title shown in the nav bar center slot (optional) */
  title?: string;
}

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

function ChevronLeftIcon() {
  return (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
  );
}

function UserCircleIcon() {
  return (
    <svg
      className="w-6 h-6"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M17.982 18.725A7.488 7.488 0 0012 15.75a7.488 7.488 0 00-5.982 2.975m11.963 0a9 9 0 10-11.963 0m11.963 0A8.966 8.966 0 0112 21a8.966 8.966 0 01-5.982-2.275M15 9.75a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Back Button
// ---------------------------------------------------------------------------

interface BackButtonProps {
  to?: string | null;
}

function BackButton({ to }: BackButtonProps) {
  const navigate = useNavigate();
  const location = useLocation();

  // Determine if we can go back in history
  const canGoBack = location.key !== 'default';

  if (to === null) return null;

  const handleBack = () => {
    if (to) {
      navigate(to);
    } else if (canGoBack) {
      navigate(-1);
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <button
      type="button"
      onClick={handleBack}
      className="
        inline-flex items-center justify-center
        -ms-2 min-h-[44px] min-w-[44px] px-2
        rounded-lg
        text-clinical-charcoal hover:text-clinical-teal
        hover:bg-clinical-cream
        transition-colors duration-150
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clinical-amber focus-visible:ring-offset-2
      "
      aria-label="Go back"
    >
      <ChevronLeftIcon />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Profile Menu
// ---------------------------------------------------------------------------

function ProfileButton() {
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    window.location.href = '/login';
  };

  const displayName =
    user && 'name' in user
      ? user.name
      : user?.email ?? 'Doctor';

  return (
    <div className="relative group">
      <button
        type="button"
        className="
          inline-flex items-center justify-center gap-2
          min-h-[44px] min-w-[44px] px-2 rounded-lg
          text-clinical-charcoal hover:text-clinical-teal
          hover:bg-clinical-cream
          transition-colors duration-150
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clinical-amber focus-visible:ring-offset-2
        "
        aria-label={`Profile menu for ${displayName}`}
        aria-haspopup="menu"
      >
        <UserCircleIcon />
      </button>

      {/* Dropdown — visible on group-hover/focus-within */}
      <div
        className="
          absolute end-0 top-full mt-1 z-50
          min-w-[160px]
          bg-white rounded-clinical shadow-clinical-md border border-clinical-border
          opacity-0 invisible
          group-hover:opacity-100 group-hover:visible
          group-focus-within:opacity-100 group-focus-within:visible
          transition-all duration-150
        "
        role="menu"
      >
        <div className="px-4 py-2.5 border-b border-clinical-border">
          <p className="text-xs font-body text-clinical-text-muted uppercase tracking-wide">Signed in as</p>
          <p className="text-sm font-body font-semibold text-clinical-charcoal truncate mt-0.5">
            {displayName}
          </p>
        </div>
        <button
          type="button"
          role="menuitem"
          onClick={() => { void handleLogout(); }}
          className="
            w-full text-start px-4 py-2.5
            text-sm font-body text-clinical-charcoal
            hover:bg-clinical-cream hover:text-clinical-teal
            transition-colors duration-150
            rounded-b-clinical
          "
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Layout
// ---------------------------------------------------------------------------

// Web idle timeout: 10 minutes per P2.9 default; config endpoint not yet
// plumbed so we use the PRD default directly.
const WEB_IDLE_TIMEOUT_MS = 10 * 60 * 1000;

export function DoctorLayout({ children, backTo, title }: DoctorLayoutProps) {
  const { logout } = useAuth();
  const navigate = useNavigate();

  useIdleTimeout({
    timeoutMs: WEB_IDLE_TIMEOUT_MS,
    onTimeout: () => {
      void logout().finally(() => {
        navigate('/login', { replace: true });
      });
    },
  });

  return (
    <div className="min-h-screen bg-[var(--color-bg)] font-body">
      {/* ------------------------------------------------------------------ */}
      {/* Top navigation bar                                                  */}
      {/* ------------------------------------------------------------------ */}
      <nav
        className="
          sticky top-0 z-40
          border-b border-clinical-border
          bg-white/90 backdrop-blur-sm
        "
        style={{ boxShadow: '0 1px 4px 0 rgba(26,26,46,0.06)' }}
        role="navigation"
        aria-label="Doctor navigation"
      >
        <div className="mx-auto max-w-2xl px-4 sm:px-6">
          <div className="flex h-14 items-center justify-between gap-2">
            {/* Left: Back button */}
            <div className="flex-none">
              <BackButton to={backTo} />
            </div>

            {/* Center: Title or wordmark */}
            <div className="flex-1 flex items-center justify-center min-w-0 px-2">
              {title ? (
                <h1 className="font-heading text-base font-semibold text-clinical-charcoal truncate">
                  {title}
                </h1>
              ) : (
                <Link
                  to="/dashboard"
                  className="font-heading text-base font-semibold text-clinical-charcoal hover:text-clinical-teal transition-colors"
                  aria-label="Clinical Interpreter — go to dashboard"
                >
                  Clinical Interpreter
                </Link>
              )}
            </div>

            {/* Right: Notification badge + Profile menu */}
            <div className="flex-none flex items-center gap-1">
              <NotificationBadge />
              <ProfileButton />
            </div>
          </div>
        </div>
      </nav>

      {/* ------------------------------------------------------------------ */}
      {/* Page content                                                         */}
      {/* ------------------------------------------------------------------ */}
      <main className="mx-auto max-w-2xl px-4 py-6 sm:px-6 sm:py-8">
        {children}
      </main>
    </div>
  );
}
